import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useAuth } from '../contexts/AuthContext'
import { Eye, EyeOff, User, Mail, Lock, Save, Key, Video } from 'lucide-react'
import api from '../services/api'
import EmbeddedCameraStream from '../components/EmbeddedCameraStream'

const Profile = () => {
    const { user, updateProfile, changePassword } = useAuth()
    const [showCurrentPassword, setShowCurrentPassword] = useState(false)
    const [showNewPassword, setShowNewPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const [isUpdatingProfile, setIsUpdatingProfile] = useState(false)
    const [isChangingPassword, setIsChangingPassword] = useState(false)
    const [userCameras, setUserCameras] = useState([])
    const [isExpanded, setIsExpanded] = useState(false)

    const {
        register: registerProfile,
        handleSubmit: handleProfileSubmit,
        formState: { errors: profileErrors },
        reset: resetProfileForm
    } = useForm({
        defaultValues: {
            username: user?.username || '',
            email: user?.email || ''
        }
    })

    // Update form values when user data changes
    useEffect(() => {
        if (user) {
            resetProfileForm({
                username: user.username || '',
                email: user.email || ''
            })
        }
    }, [user, resetProfileForm])

    const {
        register: registerPassword,
        handleSubmit: handlePasswordSubmit,
        watch,
        reset: resetPasswordForm,
        formState: { errors: passwordErrors }
    } = useForm()

    const newPassword = watch('newPassword')

    useEffect(() => {
        fetchUserCameras()
    }, [])

    const fetchUserCameras = async () => {
        try {
            const response = await api.get('/cameras/mine')
            setUserCameras(response.data.data.cameras)
        } catch (error) {
            console.error('Error fetching user cameras:', error)
        }
    }

    const onProfileSubmit = async (data, event) => {
        if (event) {
            event.preventDefault()
        }
        
        console.log('Form submitted with data:', data)
        console.log('Form validation errors:', profileErrors)
        
        setIsUpdatingProfile(true)
        try {
            console.log('Calling updateProfile with data:', data)
            const result = await updateProfile(data)
            console.log('Profile update result:', result)
            // Profile updated successfully - no alert needed
        } catch (error) {
            console.error('Profile update error:', error)
            alert(`Error updating profile: ${error.message}`)
        } finally {
            setIsUpdatingProfile(false)
        }
    }

    const onPasswordSubmit = async (data, event) => {
        if (event) {
            event.preventDefault()
        }
        
        setIsChangingPassword(true)
        try {
            await changePassword(data.currentPassword, data.newPassword)
            resetPasswordForm()
            // Password changed successfully - no alert needed
        } catch (error) {
            console.error('Password change error:', error)
            alert(`Error changing password: ${error.message}`)
        } finally {
            setIsChangingPassword(false)
        }
    }

    const toggleExpand = () => {
        setIsExpanded(!isExpanded)
    }

    return (
        <div className="space-y-8 max-w-7xl mx-auto px-2 md:px-6 py-6 bg-gradient-to-br from-indigo-50 via-cyan-50 to-indigo-100">
            <div>
                <h1 className="text-3xl font-extrabold text-indigo-700 tracking-tight">Profile Settings</h1>
                <p className="text-gray-600">Manage your account information and security</p>
            </div>

            {/* Camera Stream Section */}
  
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                {/* Profile Information */}
                <div className="bg-white rounded-xl shadow-lg border border-indigo-100 p-6">
                    <div className="mb-6">
                        <h3 className="text-xl font-bold text-indigo-700 mb-1">Profile Information</h3>
                        <p className="text-gray-500">Update your personal information</p>
                    </div>
                    <form onSubmit={handleProfileSubmit(onProfileSubmit)} className="space-y-4">
                        <div>
                            <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                                Username
                            </label>
                            <div className="mt-1 relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <User className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    id="username"
                                    type="text"
                                    className="input pl-10 w-full focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
                                    placeholder="Enter your username"
                                    {...registerProfile('username', {
                                        required: 'Username is required',
                                        minLength: {
                                            value: 3,
                                            message: 'Username must be at least 3 characters'
                                        },
                                        maxLength: {
                                            value: 30,
                                            message: 'Username must be less than 30 characters'
                                        },
                                        
                                    })}
                                />
                            </div>
                            {profileErrors.username && (
                                <p className="mt-1 text-sm text-red-600">{profileErrors.username.message}</p>
                            )}
                        </div>
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                Email address
                            </label>
                            <div className="mt-1 relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Mail className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    id="email"
                                    type="email"
                                    className="input pl-10 w-full focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
                                    placeholder="Enter your email"
                                    {...registerProfile('email', {
                                        pattern: {
                                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                            message: 'Invalid email address'
                                        }
                                    })}
                                />
                            </div>
                            {profileErrors.email && (
                                <p className="mt-1 text-sm text-red-600">{profileErrors.email.message}</p>
                            )}
                        </div>
                        <div className="pt-4">
                            <button
                                type="submit"
                                disabled={isUpdatingProfile}
                                className="btn w-full py-3 rounded-lg text-lg font-semibold shadow-md bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white transition"
                            >
                                {isUpdatingProfile ? (
                                    <div className="flex items-center justify-center">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Updating...
                                    </div>
                                ) : (
                                    <div className="flex items-center">
                                        <Save className="h-4 w-4 mr-2" />
                                        Update Profile
                                    </div>
                                )}
                            </button>
                        </div>
                    </form>
                </div>

                {/* Change Password */}
                <div className="bg-white rounded-xl shadow-lg border border-indigo-100 p-6">
                    <div className="mb-6">
                        <h3 className="text-xl font-bold text-indigo-700 mb-1">Change Password</h3>
                        <p className="text-gray-500">Update your password for security</p>
                    </div>
                    <form onSubmit={handlePasswordSubmit(onPasswordSubmit)} className="space-y-4">
                        <div>
                            <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">
                                Current Password
                            </label>
                            <div className="mt-1 relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Lock className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    id="currentPassword"
                                    type={showCurrentPassword ? 'text' : 'password'}
                                    className="input pl-10 pr-10 w-full focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
                                    placeholder="Enter current password"
                                    {...registerPassword('currentPassword', {
                                        required: 'Current password is required'
                                    })}
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                    tabIndex={-1}
                                >
                                    {showCurrentPassword ? (
                                        <EyeOff className="h-5 w-5 text-gray-400" />
                                    ) : (
                                        <Eye className="h-5 w-5 text-gray-400" />
                                    )}
                                </button>
                            </div>
                            {passwordErrors.currentPassword && (
                                <p className="mt-1 text-sm text-red-600">{passwordErrors.currentPassword.message}</p>
                            )}
                        </div>
                        <div>
                            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                                New Password
                            </label>
                            <div className="mt-1 relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Key className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    id="newPassword"
                                    type={showNewPassword ? 'text' : 'password'}
                                    className="input pl-10 pr-10 w-full focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
                                    placeholder="Enter new password"
                                    {...registerPassword('newPassword', {
                                        required: 'New password is required',
                                        minLength: {
                                            value: 6,
                                            message: 'Password must be at least 6 characters'
                                        },
                                        pattern: {
                                            value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                                            message: 'Password must contain at least one uppercase letter, one lowercase letter, and one number'
                                        }
                                    })}
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    tabIndex={-1}
                                >
                                    {showNewPassword ? (
                                        <EyeOff className="h-5 w-5 text-gray-400" />
                                    ) : (
                                        <Eye className="h-5 w-5 text-gray-400" />
                                    )}
                                </button>
                            </div>
                            {passwordErrors.newPassword && (
                                <p className="mt-1 text-sm text-red-600">{passwordErrors.newPassword.message}</p>
                            )}
                        </div>
                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                                Confirm New Password
                            </label>
                            <div className="mt-1 relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Key className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    id="confirmPassword"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    className="input pl-10 pr-10 w-full focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
                                    placeholder="Confirm new password"
                                    {...registerPassword('confirmPassword', {
                                        required: 'Please confirm your password',
                                        validate: value => value === newPassword || 'Passwords do not match'
                                    })}
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    tabIndex={-1}
                                >
                                    {showConfirmPassword ? (
                                        <EyeOff className="h-5 w-5 text-gray-400" />
                                    ) : (
                                        <Eye className="h-5 w-5 text-gray-400" />
                                    )}
                                </button>
                            </div>
                            {passwordErrors.confirmPassword && (
                                <p className="mt-1 text-sm text-red-600">{passwordErrors.confirmPassword.message}</p>
                            )}
                        </div>
                        <div className="pt-4">
                            <button
                                type="submit"
                                disabled={isChangingPassword}
                                className="btn w-full py-3 rounded-lg text-lg font-semibold shadow-md bg-gradient-to-r from-cyan-600 to-cyan-700 hover:from-cyan-700 hover:to-cyan-800 text-white transition"
                            >
                                {isChangingPassword ? (
                                    <div className="flex items-center justify-center">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Changing Password...
                                    </div>
                                ) : (
                                    <div className="flex items-center">
                                        <Key className="h-4 w-4 mr-2" />
                                        Change Password
                                    </div>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Account Information */}
            <div className="bg-white rounded-xl shadow-lg border border-indigo-100 p-6">
                <div className="mb-6">
                    <h3 className="text-xl font-bold text-indigo-700 mb-1">Account Information</h3>
                    <p className="text-gray-500">Your account details and permissions</p>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                        <label className="text-sm font-medium text-gray-500">Username</label>
                        <p className="text-sm text-gray-900">{user?.username}</p>
                    </div>
                    <div>
                        <label className="text-sm font-medium text-gray-500">Email</label>
                        <p className="text-sm text-gray-900">{user?.email}</p>
                    </div>
                    <div>
                        <label className="text-sm font-medium text-gray-500">Role</label>
                        <p className="text-sm text-gray-900">{user?.role?.name}</p>
                    </div>
                    <div>
                        <label className="text-sm font-medium text-gray-500">Account Status</label>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${user?.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                            }`}>
                            {user?.isActive ? 'Active' : 'Inactive'}
                        </span>
                    </div>
                    <div>
                        <label className="text-sm font-medium text-gray-500">Member Since</label>
                        <p className="text-sm text-gray-900">
                            {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                        </p>
                    </div>
                    <div>
                        <label className="text-sm font-medium text-gray-500">Last Login</label>
                        <p className="text-sm text-gray-900">
                            {user?.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Profile 